import { Box, Button, Checkbox, Flex, Heading, HStack, useToast, VStack, useColorModeValue } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useJobEditor } from "./hooks/useJobEditor";
import CustomerSection from "./components/CustomerSection";
import ItemSection from "./components/ItemSection";
import DescriptionSection from "./components/DescriptionSection";
import PriceSection from "./components/PriceSection";
import StaffDateSection from "./components/StaffDateSection";

const JobEditorScreen = () => {
  const navigate = useNavigate();
  const { jobNo } = useParams();
  const toast = useToast();

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const { jobData, loading, handleSave, handleSaveAndPrint, updateJobData } = useJobEditor(jobNo);

  const [flags, setFlags] = useState({
    complete: false,
    pickedUp: false,
    booking: false,
    cancelled: false,
    irreparable: false,
  });

  const [printInternal, setPrintInternal] = useState(false);

  useEffect(() => {
    if (jobData) {
      setFlags({
        complete: jobData.COMPLETE || false,
        pickedUp: jobData.PICKEDUP || false,
        booking: jobData.BOOKING || false,
        cancelled: jobData.CANCELLED || false,
        irreparable: jobData.IRREPARABLE || false,
      });
    }
  }, [jobData]);

  const handleFlagChange = (flag, value) => {
    setFlags((prev) => ({ ...prev, [flag]: value }));
    updateJobData({ [flag.toUpperCase()]: value ? 1 : 0 });
  };

  const handleSaveClick = async () => {
    try {
      const result = await handleSave();
      if (result.success) {
        toast({
          title: "Job Saved",
          description: `Job #${result.jobNo} has been saved successfully.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        navigate(`/jobs`);
      }
    } catch (err) {
      toast({
        title: "Error Saving Job",
        description: err.message || "An error occurred while saving the job.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSaveAndPrintClick = async () => {
    try {
      const result = await handleSaveAndPrint(printInternal);
      if (result.success) {
        toast({
          title: "Job Saved and Printed",
          description: `Job #${result.jobNo} has been saved and sent to printer.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        navigate(`/jobs`);
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "An error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading && !jobData) {
    return (
      <Box p={6}>
        <Heading size='md'>Loading...</Heading>
      </Box>
    );
  }

  return (
    <Box p={4}>
      {/* Header Section */}
      <Flex
        bg={bgColor}
        p={3}
        borderRadius='md'
        border='1px'
        borderColor={borderColor}
        mb={4}
        alignItems='center'
        justifyContent='space-between'
      >
        <HStack spacing={4}>
          <Checkbox isChecked={flags.booking} onChange={(e) => handleFlagChange("booking", e.target.checked)}>
            Booking
          </Checkbox>
          <Checkbox
            isChecked={flags.cancelled}
            onChange={(e) => handleFlagChange("cancelled", e.target.checked)}
            isDisabled={!flags.booking}
          >
            Cancelled
          </Checkbox>
        </HStack>

        <HStack spacing={2}>
          <Heading size='md'>Job No:</Heading>
          <Heading size='lg' color='blue.500'>
            {jobData?.JOB_NO || "New"}
          </Heading>
        </HStack>

        <HStack spacing={4}>
          <Checkbox
            isChecked={flags.complete}
            onChange={(e) => handleFlagChange("complete", e.target.checked)}
            isDisabled={flags.irreparable}
          >
            Complete
          </Checkbox>
          <Checkbox isChecked={flags.irreparable} onChange={(e) => handleFlagChange("irreparable", e.target.checked)}>
            Not Repairable
          </Checkbox>
          <Checkbox isChecked={flags.pickedUp} onChange={(e) => handleFlagChange("pickedUp", e.target.checked)}>
            Picked Up
          </Checkbox>
        </HStack>
      </Flex>

      {/* Main Content */}
      <VStack spacing={3} alignItems='stretch'>
        {/* Customer and Item Row */}
        <Flex gap={3} direction={{ base: "column", lg: "row" }}>
          <Box flex={1}>
            <CustomerSection customerData={jobData?.CUSTOMER} onUpdate={(data) => updateJobData({ CUSTOMER: data })} />
          </Box>
          <Box flex={1}>
            <ItemSection
              itemData={{
                consoleType: jobData?.CONSOLE_TYPE,
                consoleName: jobData?.CONSOLE_NAME,
                consoleModel: jobData?.CONSOLE_MODEL,
                consoleColour: jobData?.CONSOLE_COLOUR,
                consoleDrive: jobData?.CONSOLE_DRIVE,
                consoleHdd: jobData?.CONSOLE_HDD,
                consoleSerial: jobData?.CONSOLE_SERIAL,
                accessor: jobData?.ACCESSOR,
              }}
              onUpdate={(data) => updateJobData(data)}
            />
          </Box>
        </Flex>

        {/* Description and Price Row */}
        <Flex gap={3} direction={{ base: "column", lg: "row" }}>
          <Box flex={1}>
            <DescriptionSection
              descriptionData={{
                jobName1: jobData?.JOB_NAME1,
                jobName2: jobData?.JOB_NAME2,
                comments: jobData?.JOB_COMMENTS,
                jobType: jobData?.JOB_TYPE,
              }}
              consoleType={jobData?.CONSOLE_TYPE}
              onUpdate={(data) => updateJobData(data)}
            />
          </Box>
          <Box flex={1}>
            <PriceSection
              priceData={{
                price: jobData?.PRICE,
                extra: jobData?.EXTRA,
                discount: jobData?.DISCOUNT,
                paid: jobData?.PAID,
                total: jobData?.TOTAL,
                balance: jobData?.BALANCE,
                quote: jobData?.QUOTE,
              }}
              onUpdate={(data) => updateJobData(data)}
            />
          </Box>
        </Flex>

        {/* Staff and Date Row */}
        <Flex gap={3} direction={{ base: "column", lg: "row" }}>
          <Box flex={1}></Box>
          <Box flex={1}>
            <StaffDateSection
              staffDateData={{
                staffId: jobData?.STAFF_ID,
                jobDate: jobData?.JOB_DATE,
                eta: jobData?.ETA,
              }}
              onUpdate={(data) => updateJobData(data)}
            />
          </Box>
        </Flex>
      </VStack>

      {/* Footer Buttons */}
      <Flex
        bg={bgColor}
        p={3}
        borderRadius='md'
        border='1px'
        borderColor={borderColor}
        mt={4}
        alignItems='center'
        justifyContent='space-between'
      >
        <Button variant='outline' onClick={() => navigate("/jobs")}>
          Close
        </Button>

        <HStack>
          <Checkbox isChecked={printInternal} onChange={(e) => setPrintInternal(e.target.checked)}>
            Print Internal Copy only
          </Checkbox>
        </HStack>

        <HStack spacing={3}>
          <Button colorScheme='blue' onClick={handleSaveClick} isLoading={loading}>
            Save
          </Button>
          <Button colorScheme='green' onClick={handleSaveAndPrintClick} isLoading={loading}>
            Save &amp; Print
          </Button>
        </HStack>
      </Flex>
    </Box>
  );
};

export default JobEditorScreen;
